import React, { useMemo } from 'react';
import { useParseContractContext } from 'features/tasks-forms';
import {
  AdditionalWorkflowDateOption,
  PlanTask,
  useAdditionalWorkflowAiService,
  usePlanTasksService,
  useProjectDetailsFieldsService,
  ValidationError,
  useDateHelper,
} from 'nekst-api';
import { useDataLoaderNew } from 'data-loader';
import { useProjectContext } from '../../view/ProjectContext';
import { LoadingAnimation, Text, usePopupManager } from 'ui-builder';
import { VerifyDataEntry } from '../FromContract/ValidateData/types';
import { NextButton } from '../Buttons';
import useAdditionalWorkflowLaunchNavigator from './additionalWorkflowLaunchNavigator';
import AdditionalWorkflowSteps, { AdditionalWorkflowStepId } from './AdditionalWorkflowSteps';
import { useConflictsContext } from './AdditionalWorkflowLaunchWrapper';
import { ApplyParseDataValidationPopup, DetailFieldsList } from '../Shared/VerifyDataComponents';
import VerifyDataLayout from '../Shared/VerifyDataLayout';

function useVerifyDataEntriesLoader() {
  const projectContext = useProjectContext();
  const detailsFieldsService = useProjectDetailsFieldsService();
  const parsedContractContext = useParseContractContext();
  const planTaskService = usePlanTasksService();
  const dateHelper = useDateHelper();

  const plan = projectContext.plan;
  const sourcePlanId = plan?._sourcePlans?.[0]?.id;

  const { data: planTasks } = useDataLoaderNew<PlanTask[]>(
    () => sourcePlanId
      ? planTaskService.getPlanTasks(sourcePlanId)
      : Promise.resolve([]),
    { dependencies: [sourcePlanId] },
  );

  const { data: detailFields } = useDataLoaderNew(
    () => sourcePlanId
      ? detailsFieldsService.getFieldsApplicableForPlan(sourcePlanId)
      : Promise.resolve([]),
    { dependencies: [sourcePlanId] },
  );

  const startEndDates = useMemo(() => {
    if (!plan || !projectContext.data) {
      return { startDate: undefined, endDate: undefined };
    }

    if (plan.dateOption === AdditionalWorkflowDateOption.UPDATE_TRANSACTION_DATES
      && plan.customStartDate && plan.customEndDate) {
      return {
        startDate: dateHelper.toString(plan.customStartDate),
        endDate: dateHelper.toString(plan.customEndDate),
      };
    } else {
      return {
        startDate: projectContext.data.startDate
          ? dateHelper.toString(new Date(projectContext.data.startDate))
          : undefined,
        endDate: projectContext.data.endDate
          ? dateHelper.toString(new Date(projectContext.data.endDate))
          : undefined,
      };
    }
  }, [plan, projectContext.data]);

  const parseResult = parsedContractContext?.parseResult;

  const verifyDataEntries = useMemo(() => {
    if (!(parseResult && detailFields && planTasks)) {
      return undefined;
    }

    const detailsFieldsItems = detailFields.map((detailsField) => {
      let name = detailsField.name;
      if (detailsField.isProjectStartDate) {
        name += ' (Start Date)';
      } else if (detailsField.isProjectEndDate) {
        name += ' (End Date)';
      }

      return {
        detailsField,
        name,
        primary: detailsField.isProjectStartDate || detailsField.isProjectEndDate,
        parsedValue: parseResult.detailsFields[detailsField.id],
        relatedTask: planTasks.find((item) => item.linkedFieldId === detailsField.id),
      };
    });

    const startDateField = detailsFieldsItems.find((item) => item.detailsField.isProjectStartDate);
    const endDateField = detailsFieldsItems.find((item) => item.detailsField.isProjectEndDate);

    const result = [];

    if (startDateField) {
      result.push(startDateField);
    }

    if (endDateField) {
      result.push(endDateField);
    }

    result.push(
      ...detailsFieldsItems.filter(
        (item) => !item.detailsField.isProjectStartDate
          && !item.detailsField.isProjectEndDate,
      ),
    );

    return result as VerifyDataEntry[];
  }, [parseResult, detailFields, planTasks]);

  return {
    verifyDataEntries,
    fileLink: parseResult?.fileLink,
    startEndDates,
  };
}

export default function AdditionalWorkflowVerifyDataPage() {
  const {
    verifyDataEntries,
    fileLink,
    startEndDates,
  } = useVerifyDataEntriesLoader();

  const projectContext = useProjectContext();
  const conflictsContext = useConflictsContext();
  const navigator = useAdditionalWorkflowLaunchNavigator();
  const additionalWorkflowAiService = useAdditionalWorkflowAiService();
  const popupManager = usePopupManager();

  const plan = projectContext.plan;

  if (!plan || !verifyDataEntries || !projectContext.data) {
    return <LoadingAnimation />;
  }

  const projectId = projectContext.data.id;
  const planId = plan.id;

  const renderPrimaryDateOverride = (entry: VerifyDataEntry) => {
    if (entry.detailsField?.isProjectStartDate) {
      return <Text>{startEndDates.startDate || '-'}</Text>;
    } else if (entry.detailsField?.isProjectEndDate) {
      return <Text>{startEndDates.endDate || '-'}</Text>;
    } else {
      return undefined;
    }
  };

  return (
    <VerifyDataLayout
      roadmap={(
        <AdditionalWorkflowSteps
          currentStep={AdditionalWorkflowStepId.VERIFY_DATA}
          hasGroups={plan.hasGroups}
          hasConflicts={conflictsContext.hasConflicts}
        />
      )}
      nextButton={(
        <NextButton
          onClick={async () => {
            try {
              await additionalWorkflowAiService.applyDefinedData(projectId, planId);
              navigator.openFirstStep(plan.hasGroups);
            } catch (e) {
              if (e instanceof ValidationError) {
                popupManager.openPopup(<ApplyParseDataValidationPopup error={e} />);
              } else {
                throw e;
              }
            }
          }}
          key="next-button"
        />
      )}
      detailFieldsList={(
        <DetailFieldsList
          verifyDataEntries={verifyDataEntries}
          renderValueOverride={renderPrimaryDateOverride}
        />
      )}
      fileLink={fileLink}
    />
  );
}
